'use client';

/**
 * useModalTracking
 *
 * 모달 상태 트래킹 훅
 * 열림/닫힘/확인/취소 이벤트 자동 발생
 *
 * @example
 * ```tsx
 * function GuestAuthModal({ isOpen, onClose, onConfirm }) {
 *   const { handleConfirm, handleDismiss } = useModalTracking('GuestAuthModal', isOpen, {
 *     onConfirm,
 *     onDismiss: onClose,
 *   });
 *
 *   return (
 *     <Modal isOpen={isOpen}>
 *       <Button onClick={handleConfirm}>확인</Button>
 *       <Button onClick={handleDismiss}>취소</Button>
 *     </Modal>
 *   );
 * }
 * ```
 *
 * 자동 발생 이벤트:
 * - `{modalName}: Viewed` - 모달 열릴 때
 * - `{modalName}: Confirmed` - handleConfirm 호출 시
 * - `{modalName}: Dismissed` - handleDismiss 호출 시
 */
import { useCallback, useEffect, useRef } from 'react';
import { Analytics } from '../tracker';

interface UseModalTrackingOptions {
  /** 이벤트와 함께 전송할 추가 속성 */
  properties?: Record<string, unknown>;
  /** 트래킹 활성화 여부 (기본값: true) */
  enabled?: boolean;
  /** 확인 버튼 클릭 시 호출할 콜백 */
  onConfirm?: () => void;
  /** 닫기/취소 버튼 클릭 시 호출할 콜백 */
  onDismiss?: () => void;
}

interface UseModalTrackingReturn {
  /** 확인 버튼에 연결할 핸들러 (트래킹 + onConfirm 호출) */
  handleConfirm: () => void;
  /** 닫기/취소 버튼에 연결할 핸들러 (트래킹 + onDismiss 호출) */
  handleDismiss: () => void;
  /** 커스텀 이벤트 트래킹 */
  trackModalEvent: (action: string, extraProperties?: Record<string, unknown>) => void;
}

/**
 * 모달 상태 트래킹 훅
 *
 * @param modalName - 모달 이름 (이벤트 prefix로 사용)
 * @param isOpen - 모달 열림 상태
 * @param options - 옵션
 * @returns 핸들러 함수들
 */
export function useModalTracking(
  modalName: string,
  isOpen: boolean,
  options: UseModalTrackingOptions = {},
): UseModalTrackingReturn {
  const { properties = {}, enabled = true, onConfirm, onDismiss } = options;

  const hasTrackedViewRef = useRef(false);
  const prevIsOpenRef = useRef(isOpen);
  const propertiesRef = useRef(properties);
  propertiesRef.current = properties;

  // 모달 열릴 때 Viewed 이벤트
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // 닫힌 → 열린 상태로 변경될 때만 트래킹
    if (isOpen && !prevIsOpenRef.current && !hasTrackedViewRef.current) {
      Analytics.track(`${modalName}: Viewed`, propertiesRef.current);
      hasTrackedViewRef.current = true;
    }

    // 모달이 닫히면 다음 열림을 위해 초기화
    if (!isOpen) {
      hasTrackedViewRef.current = false;
    }

    prevIsOpenRef.current = isOpen;
  }, [isOpen, modalName, enabled]);

  const trackModalEvent = useCallback(
    (action: string, extraProperties: Record<string, unknown> = {}) => {
      if (!enabled) {
        return;
      }
      Analytics.track(`${modalName}: ${action}`, { ...propertiesRef.current, ...extraProperties });
    },
    [modalName, enabled],
  );

  const handleConfirm = useCallback(() => {
    trackModalEvent('Confirmed');
    onConfirm?.();
  }, [trackModalEvent, onConfirm]);

  const handleDismiss = useCallback(() => {
    trackModalEvent('Dismissed');
    onDismiss?.();
  }, [trackModalEvent, onDismiss]);

  return {
    handleConfirm,
    handleDismiss,
    trackModalEvent,
  };
}
